import { Link } from 'react-router-dom';

function Idx({ num, children }) {
  return <span className="cz-section-idx"><strong>{num}</strong>{children}</span>;
}

export default function About() {
  const principles = [
    ['I',    'Objectivity',        'Scores derived from structured, multi-source data, not gut feel.'],
    ['II',   'Fairness',           'Weights and distributions are role-aware and context-sensitive.'],
    ['III',  'Business alignment', 'Company performance influences individual ratings proportionally.'],
    ['IV',   'Accountability',     'Every override, adjustment, and decision is logged and auditable.'],
    ['V',    'Explainability',     'Employees can see exactly how their score was computed.'],
    ['VI',   'Configurability',    'Every weight, KPI, and workflow is tenant-specific.'],
    ['VII',  'Integration',        'All modules share one data model; intelligence compounds across them.'],
  ];

  return (
    <>
      {/* ══ HERO ══════════════════════════════════════════════════════ */}
      <section className="cz-section" style={{ paddingTop: 72, paddingBottom: 40 }}>
        <div className="cz-wrap">
          <Idx num="00">Masthead · About</Idx>
          <h1 className="cz-display cz-display-xl" style={{ marginTop: 18, maxWidth: 1100 }}>
            The measurement of people<br/>
            is a <em>sacred</em> problem.
          </h1>
          <p className="cz-lede" style={{ marginTop: 28, maxWidth: 64 * 8 }}>
            Careers are built and broken on a number. CalibZa exists because that number
            shouldn't come from a spreadsheet at 11pm. It should come from a discipline
            &mdash; structured, multi-source, auditable, explainable to the person on the
            receiving end.
          </p>
        </div>
      </section>

      {/* ══ § 01 · ORIGIN STORY ══════════════════════════════════════ */}
      <section className="cz-section" style={{ paddingTop: 40 }}>
        <div className="cz-wrap">
          <div className="two-col" style={{ display: 'grid', gridTemplateColumns: '260px 1fr', gap: 80, alignItems: 'start' }}>
            <div>
              <Idx num="01">Origin</Idx>
              <p className="cz-mono-label" style={{ marginTop: 18 }}>Field Journal, Vol. 01 / Issue 01 · Sept 2024</p>
            </div>

            <div style={{ columnCount: 1, columnGap: 40 }} className="origin-body">
              <p style={{
                fontFamily: 'var(--ff-display)',
                fontVariationSettings: '"opsz" 48, "SOFT" 60, "WONK" 0',
                fontSize: 22, fontWeight: 400, lineHeight: 1.4, margin: 0,
              }}>
                <span style={{
                  float: 'left', fontFamily: 'var(--ff-display)', fontStyle: 'italic',
                  fontVariationSettings: '"opsz" 144, "SOFT" 100, "WONK" 1',
                  fontSize: 104, lineHeight: 0.85, padding: '8px 10px 0 0',
                  color: 'var(--blue)',
                }}>T</span>
                he founding team had spent three calibration cycles at three different
                scale-ups arguing with spreadsheets. We watched VPs override ratings with
                fifteen-character justifications. We watched identical outputs get marked
                E+ on one team and E on another. We watched exceptional performers leave
                because their compa-ratio was 82% and nobody noticed.
              </p>

              <p style={{ marginTop: 24, fontSize: 17, lineHeight: 1.7, color: 'var(--ink)' }}>
                Then we tried the market. Lattice is good at cadence but doesn't calibrate.
                Betterworks wants to sell to the Fortune 50. Culture Amp is a survey tool
                that bolted on reviews. BambooHR is records, not intelligence. Spreadsheets
                are &mdash; it turns out &mdash; what most 300-person companies still use.
              </p>

              <p style={{ marginTop: 18, fontSize: 17, lineHeight: 1.7, color: 'var(--ink)' }}>
                So we built the platform we needed. Calibration as a first-class
                product. A distribution engine that responds to the business. Anti-gaming
                as a default. Explainability as a non-negotiable. Every other module
                (goals, feedback, surveys, PIP, HRIS, comp, succession, learning, AI,
                payroll) sits on top so the intelligence compounds.
              </p>

              <p style={{ marginTop: 18, fontSize: 17, lineHeight: 1.7, color: 'var(--ink)' }}>
                We launched publicly in April 2026 after eighteen months of private pilots
                with ten companies between 80 and 1,200 employees. Every one of those
                pilots is a paying customer today.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ══ § 02 · SEVEN PRINCIPLES (dark) ═══════════════════════════ */}
      <section className="cz-section cz-section-dark">
        <div className="cz-wrap">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 40, flexWrap: 'wrap' }}>
            <div style={{ maxWidth: 720 }}>
              <Idx num="02">Seven principles</Idx>
              <h2 className="cz-display cz-display-lg" style={{ marginTop: 14 }}>
                The design principles we <em>refuse</em> to negotiate on.
              </h2>
            </div>
            <span className="cz-mono-label" style={{ color: 'var(--muted-ink)' }}>
              Every product decision is tested against these.
            </span>
          </div>

          <ol style={{ listStyle: 'none', padding: 0, margin: '56px 0 0' }}>
            {principles.map(([num, name, body], i) => (
              <li key={num} style={{
                display: 'grid', gridTemplateColumns: '100px 300px 1fr', gap: 48,
                padding: '28px 0',
                borderBottom: i === principles.length - 1 ? 'none' : '1px solid var(--rule-dark)',
                alignItems: 'center',
              }} className="principle-row">
                <span style={{
                  fontFamily: 'var(--ff-display)', fontStyle: 'italic',
                  fontVariationSettings: '"opsz" 144, "SOFT" 100, "WONK" 1',
                  fontSize: 48, fontWeight: 400, color: 'var(--yellow)',
                  lineHeight: 1, letterSpacing: '-0.02em',
                }}>{num}</span>
                <h3 style={{
                  fontFamily: 'var(--ff-display)', fontWeight: 500,
                  fontSize: 36, margin: 0, lineHeight: 1.05,
                  letterSpacing: '-0.02em', color: 'var(--cream)',
                }}>{name}</h3>
                <p style={{ fontSize: 16, lineHeight: 1.55, color: 'var(--muted-ink)', margin: 0, maxWidth: 62 * 8 }}>
                  {body}
                </p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* ══ § 03 · PLATFORM THESIS ═══════════════════════════════════ */}
      <section className="cz-section" style={{ background: 'var(--cream-warm)', borderBlock: '1px solid var(--rule-soft)' }}>
        <div className="cz-wrap" style={{ maxWidth: 1080 }}>
          <Idx num="03">The thesis, restated</Idx>
          <div style={{ marginTop: 28, display: 'grid', gridTemplateColumns: '64px 1fr', gap: 24 }} className="two-col">
            <span style={{
              fontFamily: 'var(--ff-display)', fontStyle: 'italic',
              fontVariationSettings: '"opsz" 144, "SOFT" 100, "WONK" 1',
              fontSize: 140, color: 'var(--blue)', lineHeight: 0.4,
            }}>&ldquo;</span>
            <div>
              <p style={{
                fontFamily: 'var(--ff-display)',
                fontVariationSettings: '"opsz" 144, "SOFT" 60, "WONK" 0',
                fontSize: 'clamp(28px, 3.6vw, 44px)',
                fontWeight: 400, lineHeight: 1.25, letterSpacing: '-0.02em',
                margin: 0, color: 'var(--ink)',
              }}>
                When performance data, engagement signals, HRIS records, and compensation
                planning share a single data model, the intelligence layer becomes
                exponentially more powerful. That compounding intelligence is the moat.
              </p>
              <p className="cz-mono-label" style={{ marginTop: 28 }}>
                — Platform Thesis, from the Master Blueprint
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ══ § 04 · THE TEAM ══════════════════════════════════════════ */}
      <section className="cz-section">
        <div className="cz-wrap">
          <Idx num="04">The team</Idx>
          <h2 className="cz-display cz-display-md" style={{ marginTop: 14 }}>
            Who's behind <em>the instrument</em>.
          </h2>

          <div className="team-grid" style={{
            marginTop: 48, display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)', gap: 20,
          }}>
            {[
              ['Sakthi Vel',      'CEO & co-founder',       'ex-engineering leadership', 'S'],
              ['A. Tanaka',       'CTO & co-founder',       'ex-infrastructure at scale', 'A'],
              ['R. Okafor',       'Head of Product',        'ex-PM at an HR unicorn', 'R'],
              ['M. Chen',         'Head of Design',         'ex-editorial design, NYT', 'M'],
              ['J. Nakamura',     'Head of Research',       'ex-people analytics', 'J'],
              ['L. Fenton',       'Head of Customer',       'ex-founding CS', 'L'],
              ['D. Carlisle',     'Head of Payroll',        'ex-Gusto, CPP', 'D'],
              ['— we\'re hiring',  'Open role: GTM leader',   'SF or remote',  '+'],
            ].map(([name, role, sub, initial], i) => (
              <div key={name} style={{
                border: '1px solid var(--rule-soft)',
                background: 'var(--cream)',
                padding: '20px 20px 22px',
                display: 'flex', flexDirection: 'column', gap: 8,
                position: 'relative',
              }}>
                <div style={{
                  width: '100%', aspectRatio: '1 / 1',
                  background: i % 2 === 0 ? 'var(--blue-wash)' : 'var(--cream-warm)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  border: '1px solid var(--rule-soft)',
                }}>
                  <span style={{
                    fontFamily: 'var(--ff-display)', fontStyle: 'italic',
                    fontVariationSettings: '"opsz" 144, "SOFT" 100, "WONK" 1',
                    fontSize: 72, fontWeight: 400,
                    color: i % 2 === 0 ? 'var(--blue)' : 'var(--ink)',
                    lineHeight: 1,
                  }}>{initial}</span>
                </div>
                <h3 style={{
                  fontFamily: 'var(--ff-display)', fontWeight: 500,
                  fontSize: 19, margin: '10px 0 0', letterSpacing: '-0.01em',
                }}>{name}</h3>
                <p className="cz-mono-label" style={{ color: 'var(--blue)' }}>{role}</p>
                <p style={{ fontSize: 12, color: 'var(--muted)', margin: 0 }}>{sub}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ § 05 · BACKERS ═══════════════════════════════════════════ */}
      <section className="cz-section" style={{ paddingTop: 0 }}>
        <div className="cz-wrap">
          <Idx num="05">The backing</Idx>
          <h2 className="cz-display cz-display-md" style={{ marginTop: 14, maxWidth: 820 }}>
            Capital, from people who've actually run HR at scale.
          </h2>

          <div style={{ marginTop: 40, border: '1px solid var(--rule-soft)' }}>
            {[
              ['Series Seed',   '$6M',  'Nov 2024', 'Led by a leading Silicon Valley seed fund · Participation from operator angels at Atlassian, Stripe, Shopify'],
              ['Series A',      '$22M', 'Mar 2026', 'Led by a crossover growth fund · Strategic from a founder of a category-defining HR platform'],
            ].map(([round, amt, when, investors], i) => (
              <div key={round} style={{
                display: 'grid', gridTemplateColumns: '160px 120px 140px 1fr', gap: 32,
                padding: '28px 32px',
                borderBottom: i === 0 ? '1px solid var(--rule-soft)' : 'none',
                alignItems: 'center',
              }} className="backing-row">
                <p style={{
                  fontFamily: 'var(--ff-display)', fontWeight: 500,
                  fontSize: 20, margin: 0, letterSpacing: '-0.015em',
                }}>{round}</p>
                <p style={{
                  fontFamily: 'var(--ff-display)', fontStyle: 'italic',
                  fontVariationSettings: '"opsz" 48, "SOFT" 100, "WONK" 1',
                  fontSize: 32, margin: 0, color: 'var(--blue)',
                }}>{amt}</p>
                <p className="cz-mono-label">{when}</p>
                <p style={{ fontSize: 14, color: 'var(--muted)', margin: 0, lineHeight: 1.55 }}>{investors}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ § 06 · VALUES ════════════════════════════════════════════ */}
      <section className="cz-section" style={{ background: 'var(--cream-warm)', borderBlock: '1px solid var(--rule-soft)' }}>
        <div className="cz-wrap">
          <Idx num="06">How we work</Idx>
          <h2 className="cz-display cz-display-md" style={{ marginTop: 14, maxWidth: 820 }}>
            Values that survived first <em>contact</em> with customers.
          </h2>

          <div style={{
            marginTop: 48, display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)', gap: 2,
            background: 'var(--rule-soft)',
            border: '1px solid var(--rule-soft)',
          }} className="values-grid">
            {[
              ['Show the work',     'If our tool makes a decision, it tells you why. Every score, every flag, every recommendation.'],
              ['Customer, not user','HR leaders live with our decisions for years. We build for the person responsible, not the person clicking.'],
              ['Edit, then ship',   "We'd rather ship one feature we'll stand behind in 2030 than three we're defending next quarter."],
              ['Data is a witness', 'An audit trail exists because careers are at stake. We never ship anything that can\'t be reconstructed.'],
              ['Stop pretending',   "If a module is behind, we say so in the roadmap. No 'coming soon' pages with nothing behind them."],
              ['Build for the exit','Every tenant gets their data back. We don\'t hold migrations hostage.'],
            ].map(([head, body]) => (
              <div key={head} style={{ background: 'var(--cream)', padding: '28px 26px' }}>
                <h3 style={{
                  fontFamily: 'var(--ff-display)', fontWeight: 500,
                  fontSize: 22, margin: 0, letterSpacing: '-0.015em',
                }}>{head}</h3>
                <p style={{ fontSize: 14, color: 'var(--muted)', margin: '12px 0 0', lineHeight: 1.6 }}>{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ CTA ═══════════════════════════════════════════════════════ */}
      <section className="cz-section cz-section-dark" style={{ paddingBlock: 96, textAlign: 'center' }}>
        <div className="cz-wrap">
          <h2 className="cz-display cz-display-lg" style={{ maxWidth: 1000, marginInline: 'auto' }}>
            Careers are built on the numbers.<br/>
            <em style={{ color: 'var(--yellow)' }}>Measure them</em> like they matter.
          </h2>
          <div style={{ display: 'flex', gap: 12, marginTop: 34, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/register" className="cz-btn cz-btn-cream cz-btn-lg">
              Start free pilot <span className="cz-btn-arrow">→</span>
            </Link>
            <Link to="/contact" className="cz-btn cz-btn-outline cz-btn-lg" style={{ boxShadow: 'inset 0 0 0 1px var(--cream)', color: 'var(--cream)' }}>
              Say hello
            </Link>
          </div>
        </div>
      </section>

      <style>{`
        @media (max-width: 1024px) {
          .team-grid { grid-template-columns: 1fr 1fr !important; }
          .values-grid { grid-template-columns: 1fr 1fr !important; }
          .principle-row { grid-template-columns: 70px 1fr !important; gap: 16px !important; }
          .principle-row > *:nth-child(3) { grid-column: 2 !important; }
          .backing-row { grid-template-columns: 1fr !important; gap: 6px !important; }
        }
        @media (max-width: 640px) {
          .team-grid { grid-template-columns: 1fr 1fr !important; }
          .values-grid { grid-template-columns: 1fr !important; }
          .two-col { grid-template-columns: 1fr !important; gap: 24px !important; }
        }
      `}</style>
    </>
  );
}
